import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MediaCard from '@/components/base/MediaCard';
import NewsCard from '@/components/base/NewsCard';
import SearchBox from '@/components/base/SearchBox';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { mediaList } from '@/mocks/movies';
import { newsList } from '@/mocks/news';

const typeTabs = [
  { key: 'all', label: '全部' },
  { key: 'media', label: '影视' },
  { key: 'news', label: '资讯' },
];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('all');
  const keyword = (searchParams.get('q') ?? '').trim();

  useDocumentMeta({
    title: keyword
      ? `“${keyword}”的搜索结果 - 粉色水蜜桃影视大全`
      : '搜索影视与资讯 - 粉色水蜜桃影视大全',
    description: keyword
      ? `粉色水蜜桃为你找到与“${keyword}”相关的影视作品与影视资讯，支持按影视、资讯分类查看结果。`
      : '在粉色水蜜桃搜索电视剧、电影、演员与影视资讯，快速找到你想看的内容。',
    keywords: `粉色水蜜桃,影视搜索,${keyword || '电视剧搜索'},电影搜索,演员搜索`,
  });

  const mediaResults = useMemo(() => {
    if (!keyword) return [];
    return mediaList.filter(
      (item) =>
        item.title.includes(keyword) ||
        item.summary.includes(keyword) ||
        item.cast.some((c) => c.name.includes(keyword)) ||
        item.genres.some((g) => g.includes(keyword)) ||
        item.tags.some((t) => t.includes(keyword)),
    );
  }, [keyword]);

  const newsResults = useMemo(() => {
    if (!keyword) return [];
    return newsList.filter(
      (item) =>
        item.title.includes(keyword) ||
        item.summary.includes(keyword) ||
        item.content.some((p) => p.includes(keyword)),
    );
  }, [keyword]);

  const showMedia = activeTab === 'all' || activeTab === 'media';
  const showNews = activeTab === 'all' || activeTab === 'news';
  const total = (showMedia ? mediaResults.length : 0) + (showNews ? newsResults.length : 0);
  const recommendations = [...mediaList].sort((a, b) => b.heat - a.heat).slice(0, 4);

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <span className="text-foreground-800">搜索结果</span>
      </nav>

      <div className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {keyword ? (
            <>
              搜索关键字：<span className="text-primary-600">{keyword}</span>
            </>
          ) : (
            '站内搜索'
          )}
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          支持影视标题、演员名与资讯关键词搜索（演示数据）
        </p>
      </div>

      <div className="mt-6 max-w-3xl">
        <SearchBox variant="page" initialValue={keyword} />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full border border-background-200 bg-background-100 p-1">
          {typeTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap rounded-full px-5 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === tab.key
                  ? 'bg-background-50 text-primary-600'
                  : 'text-foreground-600 hover:text-primary-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {keyword ? (
          <span className="text-sm text-foreground-500">
            共找到 <span className="font-semibold text-primary-600">{total}</span> 条结果
          </span>
        ) : null}
      </div>

      {!keyword ? (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-50 py-20 text-center">
          <i className="ri-search-line text-4xl text-foreground-400" aria-hidden="true"></i>
          <p className="mt-3 text-base font-medium text-foreground-800">
            输入关键词开始搜索
          </p>
          <p className="mt-1 text-sm text-foreground-500">
            试试搜索「长安十二辰」「雾都黎明」或演员名字
          </p>
        </div>
      ) : total === 0 ? (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-50 py-16 text-center">
          <i className="ri-emotion-sad-line text-4xl text-foreground-400" aria-hidden="true"></i>
          <p className="mt-3 text-base font-medium text-foreground-800">暂无相关影视作品</p>
          <p className="mt-1 text-sm text-foreground-500">
            没有找到与「{keyword}」相关的内容，换个关键词试试
          </p>
        </div>
      ) : (
        <div className="mt-8 space-y-10">
          {showMedia && mediaResults.length > 0 ? (
            <section>
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                影视作品
                <span className="ml-2 text-sm font-normal text-foreground-500">
                  {mediaResults.length} 部
                </span>
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
                {mediaResults.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          ) : null}

          {showNews && newsResults.length > 0 ? (
            <section>
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                相关资讯
                <span className="ml-2 text-sm font-normal text-foreground-500">
                  {newsResults.length} 篇
                </span>
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {newsResults.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}

      <section className="mt-12">
        <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
          热门影视推荐
        </h2>
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
          {recommendations.map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}
        </div>
      </section>
    </div>
  );
}